import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * DashboardActionsContext
 *
 * Root-provider pattern: DashboardActionsRoot sits above NavigationHeader and
 * Routes in App.js. Station pages call useSetDashboardActions() to register
 * their refresh/share handlers via useEffect; the nav reads them via
 * useDashboardActions(). On pages that don't register handlers the buttons
 * render disabled (null values).
 */
const ReadContext = createContext({ onRefresh: null, onShare: null, isRefreshing: false });
const WriteContext = createContext(() => {});

export const DashboardActionsRoot = ({ children }) => {
    const [state, setState] = useState({ onRefresh: null, onShare: null, isRefreshing: false });
    const setActions = useCallback((next) => {
        setState((prev) => ({ ...prev, ...next }));
    }, []);
    const value = useMemo(() => state, [state]);
    return (
        <ReadContext.Provider value={value}>
            <WriteContext.Provider value={setActions}>
                {children}
            </WriteContext.Provider>
        </ReadContext.Provider>
    );
};

/**
 * Returns { onRefresh, onShare, isRefreshing }.
 * NavigationHeader uses this; buttons are disabled when values are null.
 */
export const useDashboardActions = () => useContext(ReadContext);

/**
 * Returns the setState dispatcher.
 * Pages call this in a useEffect to register / deregister their handlers.
 */
export const useSetDashboardActions = () => useContext(WriteContext);

export default ReadContext;
